import * as THREE from 'three/webgpu';
import { formatMeasurePairLengthDisplay } from '../math/MeasureNumericKernel';
import type { MeasurePairMeasurementCalloutItem } from '../types/measure-callout-types';
import { MeasurePairMeasurementCalloutKind } from '../types/measure-callout-types';
import {
    hasRenderableMeasureSegmentLength,
    MeasureDxDyDzRenderableSegmentKind,
    resolveRenderableDxDyDzSegments,
} from '../utils/measureDxDyDzRenderableSegments';

/** 直线距离结果项 ID。 */
export const MEASURE_PAIR_CALLOUT_ID_DISTANCE = 'measure-pair:distance';

/** ΔX 结果项 ID。 */
export const MEASURE_PAIR_CALLOUT_ID_DX = 'measure-pair:dx';

/** ΔY 结果项 ID。 */
export const MEASURE_PAIR_CALLOUT_ID_DY = 'measure-pair:dy';

/** ΔZ 结果项 ID。 */
export const MEASURE_PAIR_CALLOUT_ID_DZ = 'measure-pair:dz';

/** 垂直距离结果项 ID。 */
export const MEASURE_PAIR_CALLOUT_ID_NORMAL_DISTANCE = 'measure-pair:normal-distance';

/**
 * 构建双选测量结果项的输入。
 */
export interface BuildPairMeasurementCalloutsInput {
    /** 直线距离线起点（世界坐标，米）。 */
    from: THREE.Vector3;
    /** 直线距离线终点（世界坐标，米）。 */
    to: THREE.Vector3;
    /** X 分量差（场景米制，用于分量线几何）。 */
    dxMeters: number;
    /** Y 分量差（场景米制）。 */
    dyMeters: number;
    /** Z 分量差（场景米制）。 */
    dzMeters: number;
    /** X 分量差（毫米，用于结果展示）。 */
    dxMillimeter: number;
    /** Y 分量差（毫米）。 */
    dyMillimeter: number;
    /** Z 分量差（毫米）。 */
    dzMillimeter: number;
    /** 直线距离（毫米）。 */
    distanceMillimeter: number;
    /** 是否在结果区列出 ΔX / ΔY / ΔZ。 */
    showDxDyDz: boolean;
    /** 垂直距离（毫米）；仅在与直线距离不等时传入。 */
    normalDistanceMillimeter?: number;
    /** 垂直距离线起点。 */
    normalFrom?: THREE.Vector3;
    /** 垂直距离线终点。 */
    normalTo?: THREE.Vector3;
}

/**
 * 计算两世界坐标点的中点。
 *
 * @param start 起点。
 * @param end 终点。
 * @returns 中点副本。
 */
function resolveSegmentMidpoint(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3 {
    return start.clone().add(end).multiplyScalar(0.5);
}

/**
 * 构建单条双选测量结果项。
 *
 * @param calloutId 结果项唯一 ID。
 * @param kind 结果项种类。
 * @param label 展示标签。
 * @param value 展示值。
 * @param lineStart 对应测量线起点。
 * @param lineEnd 对应测量线终点。
 * @returns 结果项。
 */
function buildPairMeasurementCalloutItem(
    calloutId: string,
    kind: MeasurePairMeasurementCalloutKind,
    label: string,
    value: string,
    lineStart: THREE.Vector3,
    lineEnd: THREE.Vector3,
): MeasurePairMeasurementCalloutItem {
    return {
        calloutId,
        kind,
        label,
        value,
        worldAnchor: resolveSegmentMidpoint(lineStart, lineEnd),
    };
}

/**
 * 基于当前双选测量数值与显示策略构建结果区条目。
 *
 * 设计说明（对齐 Onshape）：
 * 1. 结果项供测量浮窗下方展示，点选后驱动三维虚线可见性；
 * 2. Show XYZ 开启时始终列出 ΔX / ΔY / ΔZ 三行（含零值），不再因与距离线重合而省略。
 *
 * @param input 构建输入。
 * @returns 结果项列表。
 */
export function buildPairMeasurementCallouts(
    input: BuildPairMeasurementCalloutsInput,
): MeasurePairMeasurementCalloutItem[] {
    const {
        from,
        to,
        dxMeters,
        dyMeters,
        dzMeters,
        dxMillimeter,
        dyMillimeter,
        dzMillimeter,
        distanceMillimeter,
        showDxDyDz,
        normalDistanceMillimeter,
        normalFrom,
        normalTo,
    } = input;

    const callouts: MeasurePairMeasurementCalloutItem[] = [
        buildPairMeasurementCalloutItem(
            MEASURE_PAIR_CALLOUT_ID_DISTANCE,
            MeasurePairMeasurementCalloutKind.Distance,
            '距离',
            formatMeasurePairLengthDisplay(distanceMillimeter),
            from,
            to,
        ),
    ];

    if (
        normalDistanceMillimeter !== undefined
        && normalFrom
        && normalTo
        && hasRenderableMeasureSegmentLength(normalFrom, normalTo)
    ) {
        callouts.push(
            buildPairMeasurementCalloutItem(
                MEASURE_PAIR_CALLOUT_ID_NORMAL_DISTANCE,
                MeasurePairMeasurementCalloutKind.NormalDistance,
                '垂直距离',
                formatMeasurePairLengthDisplay(normalDistanceMillimeter),
                normalFrom,
                normalTo,
            ),
        );
    }

    if (!showDxDyDz) {
        return callouts;
    }

    const segments = resolveRenderableDxDyDzSegments({
        from,
        to,
        dx: dxMeters,
        dy: dyMeters,
        dz: dzMeters,
    });
    const segmentByKind = new Map(segments.map((segment) => [segment.kind, segment]));

    // 始终列出三个分量行；零长分量用 from 作为退化锚点
    const xyzKinds: readonly MeasureDxDyDzRenderableSegmentKind[] = [
        MeasureDxDyDzRenderableSegmentKind.Dx,
        MeasureDxDyDzRenderableSegmentKind.Dy,
        MeasureDxDyDzRenderableSegmentKind.Dz,
    ];
    xyzKinds.forEach((kind) => {
        const metadata = resolveDxDyDzCalloutMetadata(
            kind,
            dxMillimeter,
            dyMillimeter,
            dzMillimeter,
        );
        const segment = segmentByKind.get(kind);
        const lineStart = segment?.start ?? from;
        const lineEnd = segment?.end ?? from;
        callouts.push(
            buildPairMeasurementCalloutItem(
                metadata.calloutId,
                metadata.kind,
                metadata.label,
                metadata.value,
                lineStart,
                lineEnd,
            ),
        );
    });

    return callouts;
}

/**
 * 解析分量结果项元数据。
 *
 * @param kind 分量线种类。
 * @param dxMillimeter X 分量毫米值。
 * @param dyMillimeter Y 分量毫米值。
 * @param dzMillimeter Z 分量毫米值。
 * @returns 结果项元数据。
 */
function resolveDxDyDzCalloutMetadata(
    kind: MeasureDxDyDzRenderableSegmentKind,
    dxMillimeter: number,
    dyMillimeter: number,
    dzMillimeter: number,
): {
    calloutId: string;
    kind: MeasurePairMeasurementCalloutKind;
    label: string;
    value: string;
} {
    switch (kind) {
        case MeasureDxDyDzRenderableSegmentKind.Dx:
            return {
                calloutId: MEASURE_PAIR_CALLOUT_ID_DX,
                kind: MeasurePairMeasurementCalloutKind.Dx,
                label: 'ΔX',
                value: formatMeasurePairLengthDisplay(dxMillimeter),
            };
        case MeasureDxDyDzRenderableSegmentKind.Dy:
            return {
                calloutId: MEASURE_PAIR_CALLOUT_ID_DY,
                kind: MeasurePairMeasurementCalloutKind.Dy,
                label: 'ΔY',
                value: formatMeasurePairLengthDisplay(dyMillimeter),
            };
        case MeasureDxDyDzRenderableSegmentKind.Dz:
        default:
            return {
                calloutId: MEASURE_PAIR_CALLOUT_ID_DZ,
                kind: MeasurePairMeasurementCalloutKind.Dz,
                label: 'ΔZ',
                value: formatMeasurePairLengthDisplay(dzMillimeter),
            };
    }
}
